import { useCallback, useEffect, useState } from 'react';
import { useToast } from '@jywer/ui';

interface PendingRow {
  id: string;
  subscription_type: string;
  monthly_price: string;
  cancellation_end_date: string | null;
  cancellation_reason: string | null;
  client_id: string;
  client_name: string | null;
}

interface RecentRow extends PendingRow {
  cancelled_at: string | null;
}

const SUB_TYPE_LABEL: Record<string, string> = {
  basic_hosting_support: 'Basic abo',
  seo_aeo: 'SEO/AEO',
  google_ads: 'Google Ads',
  meta_ads: 'Meta',
  linkedin_ads: 'LinkedIn Ads',
  linkedin_profilpleje: 'LinkedIn Profilpleje',
  maas: 'MaaS',
  other: 'Andet',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('da-DK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * V2.0 overnight 3 scope H — banner at the top of /admin/billing showing:
 *   - Pending cancellations (status='cancelling', end_date ≤ +30d)
 *   - Recently cancelled, not yet acknowledged (status='cancelled',
 *     cancelled_at within last 14 days, cancellation_acknowledged_at=null)
 *
 * Each recently-cancelled row carries an "Kvittér" button that POSTs
 * `/api/billing/cancellations/:id/acknowledge` and removes the row.
 *
 * The banner self-hides when both lists are empty.
 */
export function CancellationBanner() {
  const toast = useToast();
  const [pending, setPending] = useState<PendingRow[]>([]);
  const [recent, setRecent] = useState<RecentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetch('/api/billing/pending-cancellations');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = (await r.json()) as { pending: PendingRow[]; recent: RecentRow[] };
      setPending(d.pending);
      setRecent(d.recent);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ukendt fejl');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function acknowledge(id: string) {
    try {
      const r = await fetch(`/api/billing/cancellations/${id}/acknowledge`, { method: 'POST' });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        toast.showError(body.error ?? `HTTP ${r.status}`);
        return;
      }
      await load();
    } catch (e) {
      toast.showError(e instanceof Error ? e.message : 'Ukendt fejl');
    }
  }

  if (loading || err) return null;
  if (pending.length === 0 && recent.length === 0) return null;

  return (
    <section
      className="flex flex-col gap-2 rounded border border-status-warn/40 bg-status-warn-tint/40 px-4 py-3"
      aria-label="Opsigelses-overblik"
    >
      <h2 className="text-sm font-semibold text-status-warn">Opsigelser</h2>

      {pending.length > 0 ? (
        <details>
          <summary className="cursor-pointer text-sm text-ink-1">
            <span className="font-medium">
              {pending.length} abonnement
              {pending.length === 1 ? '' : 'er'} ophører inden 30 dage
            </span>{' '}
            <span className="text-ink-2">— bekræft i Dinero efter ophør</span>
          </summary>
          <ul className="mt-1 flex flex-col gap-1 pl-4 text-xs text-ink-1">
            {pending.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2">
                <span>
                  <span className="font-medium">{r.client_name ?? '—'}</span>
                  <span className="ml-1 text-ink-2">
                    ({SUB_TYPE_LABEL[r.subscription_type] ?? r.subscription_type})
                  </span>
                </span>
                <span className="tabular-nums text-ink-2">
                  ophører {formatDate(r.cancellation_end_date)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {recent.length > 0 ? (
        <details open>
          <summary className="cursor-pointer text-sm text-ink-1">
            <span className="font-medium">
              {recent.length} abonnement{recent.length === 1 ? ' er' : 'er er'} ophørt
            </span>{' '}
            <span className="text-ink-2">— klar til Dinero offboarding</span>
          </summary>
          <ul className="mt-1 flex flex-col gap-1 pl-4 text-xs">
            {recent.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-2 rounded border border-line-1 bg-paper-0 px-2 py-1"
              >
                <span>
                  <span className="font-medium text-ink-1">{r.client_name ?? '—'}</span>
                  <span className="ml-1 text-ink-2">
                    ({SUB_TYPE_LABEL[r.subscription_type] ?? r.subscription_type})
                  </span>
                  <span className="ml-2 text-ink-2">ophørt {formatDate(r.cancelled_at)}</span>
                </span>
                <button
                  type="button"
                  onClick={() => acknowledge(r.id)}
                  className="rounded border border-line-2 px-2 py-0.5 text-xs hover:border-primary"
                >
                  Kvittér
                </button>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
